// What a zip holds, for the preview (FilePreview.tsx), without downloading it.
// Ported from Space (C:\Github\Space\app\src\lib\zip.ts).
import { HttpError } from '../api.ts';
import { plural } from '../format.ts';
import { readProblem } from './model.ts';

export interface ZipEntry {
  /** "docs/2026/report.pdf"; folders end with "/". */
  path: string;
  size: number;
}

/** The largest list read: room for 65 534 entries (the most without zip64) of 500 bytes each. */
export const LIST_LIMIT = 32 * 1024 * 1024;

/**
 * The zip's list of files. A zip keeps that list (the central directory) at its very end, so a request
 * for the last 64 KB, and sometimes one more for the list itself, is enough.
 * Zip64 (over 4 GB or 65 535 entries) is not read: it throws. `signal`: leaving the file stops the requests.
 * The end record says where the list is: one pointing anywhere but before itself, or at more than LIST_LIMIT,
 * throws before the second request (a crafted one would download the whole file).
 */
export async function listZip(url: string, size: number, signal?: AbortSignal): Promise<ZipEntry[]> {
  const tailStart = Math.max(0, size - 22 - 0xffff);
  const tail = await range(url, tailStart, size - 1, signal);
  const view = new DataView(tail);

  const end = endOf(view);
  if (end < 0) throw new Error('This is not a zip file.');
  const count = view.getUint16(end + 10, true);
  const listSize = view.getUint32(end + 12, true);
  const listStart = view.getUint32(end + 16, true);
  if (count === 0xffff || listStart === 0xffffffff) throw new Error('This zip is too large to list here (zip64).');
  if (!count) return [];
  // The list ends where the end record starts (or before it: bytes in front of the zip).
  if (!listSize || listStart + listSize > tailStart + end) throw new Error('This zip is damaged.');
  if (listSize > LIST_LIMIT) throw new Error("This zip's list of files is too large to show here.");

  /** `n` bytes from `start`: cut from the tail when it holds them, else asked. */
  const read = (start: number, n = listSize) =>
    start >= tailStart ? tail.slice(start - tailStart, start - tailStart + n) : range(url, start, start + n - 1, signal);
  // Bytes in front of the zip are not in its offsets: the list is then where it ends, just before the end record.
  // The place the end record gives is tried with its first 4 bytes only: the list itself is read once.
  const real = tailStart + end - listSize;
  const list = await read(real === listStart || startsList(await read(listStart, 4)) ? listStart : real);
  const d = new DataView(list);

  const entries: ZipEntry[] = [];
  for (const p of records(d, 0)) {
    if (entries.length >= count) break;
    const nameLength = d.getUint16(p + 28, true);
    const path = nameOf(new Uint8Array(list, p + 46, Math.min(nameLength, list.byteLength - p - 46)));
    entries.push({ path, size: d.getUint32(p + 24, true) });
  }
  // The end record counts entries (an empty zip returned above): no record at either place is a damaged list.
  if (!entries.length) throw new Error('This zip is damaged.');
  return entries;
}

/**
 * Code page 775 (Baltic DOS), bytes 0x80–0xFF: Windows in Lithuanian writes an old zip's names so (Explorer,
 * 7-Zip). Its é ü ä ö Ä Ö Ü are those of code page 437 (Windows in English) too.
 */
const CP775 =
  'ĆüéāäģåćłēŖŗīŹÄÅÉæÆōöĢ¢ŚśÖÜø£Ø×¤' +
  'ĀĪóŻżź”¦©®¬½¼Ł«»░▒▓│┤ĄČĘĖ╣║╗╝ĮŠ┐' +
  '└┴┬├─┼ŲŪ╚╔╩╦╠═╬Žąčęėįšųūž┘┌█▄▌▐▀' +
  'ÓßŌŃõÕµńĶķĻļņĒŅ’\u00ad±“¾¶§÷„°∙·¹³²■\u00a0';

const UTF8 = new TextDecoder('utf-8', { fatal: true });

/** An entry's name: UTF-8 (new zips), else code page 775 (CP775). */
function nameOf(bytes: Uint8Array): string {
  try {
    return UTF8.decode(bytes);
  } catch {
    return Array.from(bytes, (b) => (b < 0x80 ? String.fromCharCode(b) : CP775[b - 0x80])).join('');
  }
}

/** Where the end record (signature 0x06054b50) starts, searched from the back: a comment may follow it. -1: none. */
function endOf(view: DataView): number {
  for (let i = view.byteLength - 22; i >= 0; i--) if (view.getUint32(i, true) === 0x06054b50) return i;
  return -1;
}

/** The bytes start with a record of the list (signature 0x02014b50). */
function startsList(bytes: ArrayBuffer): boolean {
  return bytes.byteLength >= 4 && new DataView(bytes).getUint32(0, true) === 0x02014b50;
}

/** Where each record of the list (signature 0x02014b50) starts, one after another from `p`, while the signature is there. */
function* records(view: DataView, p: number): Generator<number> {
  while (p + 46 <= view.byteLength && view.getUint32(p, true) === 0x02014b50) {
    yield p;
    p += 46 + view.getUint16(p + 28, true) + view.getUint16(p + 30, true) + view.getUint16(p + 32, true);
  }
}

/**
 * A Word, Excel or PowerPoint file (a zip) goes to its library only if it unpacks to at most `bytes`, in at most
 * `parts` parts, none over `free` growing more than `ratio` times: a few KB of zip can unpack to gigabytes.
 */
export const UNPACK_LIMIT = { bytes: 100 * 1024 * 1024, parts: 5000, ratio: 100, free: 1024 * 1024 };

/** Word and PowerPoint: drawn into the page, on its thread, where nothing can stop them: less than Excel (read in a worker). */
export const DRAWN_LIMIT = { ...UNPACK_LIMIT, bytes: 30 * 1024 * 1024 };

/**
 * Whether a Word, Excel or PowerPoint file (the whole zip) is within `limit` (UNPACK_LIMIT, DRAWN_LIMIT). Its library unpacks every part
 * with no limit. Each part is unpacked here and counted, stopping past the limit: the sizes the zip's list gives
 * can be false, and must then match. Read as the libraries read it (JSZip, fflate); one they would read otherwise
 * (zip64, bytes before the list, a part neither stored nor deflated, broken data) is false. Parts sharing bytes
 * are false too: each byte of the zip is unpacked once at most. `signal`: leaving the file stops it (throws).
 * Not a zip at all: true (the library says it cannot read it).
 */
export async function unpacksSmall(data: ArrayBuffer, limit = UNPACK_LIMIT, signal?: AbortSignal): Promise<boolean> {
  const view = new DataView(data);
  const end = endOf(view);
  if (end < 0) return true;
  // 0xffff or 0xffffffff in any of them: zip64, whose own records JSZip would read instead. fflate reads them
  // whenever a zip64 locator (0x07064b50) is just before the end record: in a part's comment too.
  const counts = [4, 6, 8, 10].map((at) => view.getUint16(end + at, true));
  const listSize = view.getUint32(end + 12, true);
  const listStart = view.getUint32(end + 16, true);
  if (counts.includes(0xffff) || listStart + listSize !== end) return false;
  if (end >= 20 && view.getUint32(end - 20, true) === 0x07064b50) return false;
  const parts: number[] = [];
  for (const p of records(view, listStart)) if (parts.push(p) > limit.parts) return false;
  // fflate reads as many records as the end record says, signature or not.
  if (parts.length < Math.max(counts[2]!, counts[3]!)) return false;

  let left = limit.bytes;
  // The zip's bytes not unpacked yet: a part may not reuse another's.
  let unread = data.byteLength;
  for (const p of parts) {
    signal?.throwIfAborted();
    const method = view.getUint16(p + 10, true);
    const packed = view.getUint32(p + 20, true);
    // The size the list gives: fflate makes room for that much before unpacking.
    const said = view.getUint32(p + 24, true);
    const local = view.getUint32(p + 42, true);
    if (packed === 0xffffffff || said === 0xffffffff || local + 30 > data.byteLength) return false;
    if (view.getUint32(local, true) !== 0x04034b50) return false;
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    if (start + packed > data.byteLength || (unread -= packed) < 0) return false;
    const most = Math.min(left, Math.max(limit.free, packed * limit.ratio));
    const size = method === 0 || packed === 0 ? packed : method === 8 ? await inflatedSize(new Uint8Array(data, start, packed), most, signal) : -1;
    signal?.throwIfAborted();
    // A size other than the list's: JSZip refuses the file then.
    if (size < 0 || size > most || size !== said) return false;
    left -= size;
  }
  return true;
}

/** How many bytes raw deflate data unpacks to, stopping once past `most` (a little more is counted then). -1: broken data, or stopped (`signal`). */
async function inflatedSize(packed: Uint8Array<ArrayBuffer>, most: number, signal?: AbortSignal): Promise<number> {
  // Fed 16 KB at a time: a piece is unpacked whole, and 16 KB unpacks to 17 MB at most.
  let at = 0;
  const input = new ReadableStream<Uint8Array<ArrayBuffer>>({
    pull(c) {
      if (signal?.aborted) c.error(signal.reason);
      else if (at >= packed.length) c.close();
      else c.enqueue(packed.subarray(at, (at += 16384)));
    },
  });
  const reader = input.pipeThrough(new DecompressionStream('deflate-raw')).getReader();
  let size = 0;
  try {
    for (let r = await reader.read(); !r.done; r = await reader.read()) {
      size += r.value.byteLength;
      if (size > most) {
        reader.cancel().catch(() => undefined);
        break;
      }
    }
    return size;
  } catch {
    return -1;
  }
}

/** Bytes `start`..`end` of the file. A server that ignores Range sends everything; the part is cut out then. */
async function range(url: string, start: number, end: number, signal?: AbortSignal): Promise<ArrayBuffer> {
  const res = await fetch(url, { headers: { Range: `bytes=${start}-${end}` }, signal });
  if (!res.ok) throw new HttpError(res.status, readProblem(res.status));
  const data = await res.arrayBuffer();
  return res.status === 206 ? data : data.slice(start, end + 1);
}

export interface ZipNode {
  name: string;
  size: number;
  /** null: a file. */
  children: ZipNode[] | null;
}

/** Folders deeper than this are one: the rest of the path is its name. A crafted name can nest 30 000 deep. */
export const MAX_DEPTH = 64;

/**
 * An entry's folders, outermost first, and its file's name (null: the entry is a folder).
 *   "a/b/c.txt" → [a, b] c.txt      "a/b/" → [a, b] null      deeper than MAX_DEPTH: the last folder holds the rest
 */
function partsOf(entry: string): { dirs: string[]; file: string | null } {
  const parts = entry.replace(/\\/g, '/').split('/');
  const file = parts.pop() || null;
  const dirs = parts.length > MAX_DEPTH ? [...parts.slice(0, MAX_DEPTH - 1), parts.slice(MAX_DEPTH - 1).join('/')] : parts;
  return { dirs, file };
}

/** A zip's list as a tree, and how many files and folders it holds (every folder named in a path, as the tree has them). */
export interface ZipTree {
  nodes: ZipNode[];
  files: number;
  folders: number;
}

/** Folders past this many: a new one is the last, the rest of the path is its name. 65 535 crafted names can make 4 million. */
export const MAX_FOLDERS = 50_000;

/** Entries → a tree, folders first, each level by name. Folders a zip only implies (no own entry) are added. */
export function zipTree(entries: ZipEntry[]): ZipTree {
  let files = 0;
  const root: ZipNode = { name: '', size: 0, children: [] };
  const all = [root];
  // Each folder's folders, by name: found by walking down, not by their whole path (32 MB of crafted names makes
  // gigabytes of those).
  const inside = new Map<ZipNode, Map<string, ZipNode>>();
  for (const e of entries) {
    const { dirs, file } = partsOf(e.path);
    let parent = root;
    for (let i = 0; i < dirs.length; i++) {
      let named = inside.get(parent);
      if (!named) inside.set(parent, (named = new Map()));
      // Past MAX_FOLDERS (the root is in `all` too), a folder not there yet holds the rest of the path.
      const last = all.length > MAX_FOLDERS && !named.has(dirs[i]!);
      const name = last ? dirs.slice(i).join('/') : dirs[i]!;
      let node = named.get(name);
      if (!node) {
        node = { name, size: 0, children: [] };
        named.set(name, node);
        parent.children!.push(node);
        all.push(node);
      }
      parent = node;
      if (last) break;
    }
    if (file === null) continue;
    parent.children!.push({ name: file, size: e.size, children: null });
    files++;
  }
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  for (const folder of all) folder.children!.sort((a, b) => Number(!a.children) - Number(!b.children) || collator.compare(a.name, b.name));
  // The root is in `all` too.
  return { nodes: root.children!, files, folders: all.length - 1 };
}

/** "3 files · 1 folder": what a list holds (zipTree's counts). */
export function zipSummary({ files, folders }: ZipTree): string {
  return [plural(files, 'file'), folders ? plural(folders, 'folder') : ''].filter(Boolean).join(' · ');
}
